import { useMemo } from 'react';

import type {
  SortByAlternativeKey,
  SortDirection,
} from '@shared/helpers/tree.helpers';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import type {
  Baseline,
  OneOfElementListChildren,
  OneOfListElements,
} from '@shared/models/project.model';
import type { GroupSectionData } from '@ui/ElementList/list.model';
import type { GroupSectionMeta } from '@features/elements/element-grouping.model';
import type { SortElementsOptions } from '@client/hooks/use-filter-elements.hooks';
import type { GroupSectionProps } from './element-groups.model';

/** Minimum shape from {@link useGroupSectionProps} needed to compute sorted sections */
export type GroupPropsForSortedElementSections = Pick<
  GroupSectionProps,
  'groupSectionBuilder'
>;

export type SortedElementGroupsSortBy =
  SortByAlternativeKey<SortElementsOptions>;

/**
 * Section returned by {@link getSortedElementGroupSections}. `items` is the
 * **full** root membership of the group (used to compute group-level totals so
 * search filtering does not affect the CO₂/cost bar). `visibleItems` is the
 * search-filtered, sort-respecting subset to render inside the group.
 */
export type SortedElementGroupSection = GroupSectionData<
  OneOfListElements,
  GroupSectionMeta
> & {
  visibleItems: OneOfListElements[];
};

export type UseSortedElementGroupsParams = {
  groupProps: GroupPropsForSortedElementSections | undefined;
  version: Baseline | undefined;
  sortedRootElements: readonly OneOfElementListChildren[];
  sortBy: SortedElementGroupsSortBy;
  direction?: SortDirection;
};

/**
 * Builds grouped element-list sections, then applies the active sort
 * (name / CO₂ / cost / position) to the groups themselves.
 *
 * Sections are built from the **full** root element list (`version.elements`)
 * so group `items` and meta (resultsSum / proposalResultsSum) reflect the
 * group's true totals — search filtering does not shrink the CO₂/cost bars.
 * Each section also gets a `visibleItems` field (the search-filtered,
 * sort-respecting subset to render); sections whose visibleItems would be
 * empty are dropped so empty group headers don't show under search.
 *
 * @param groupProps When absent, returns an empty list
 * @param version When absent, returns an empty list (same guard as the list view)
 */
export const getSortedElementGroupSections = (
  groupProps: GroupPropsForSortedElementSections | undefined,
  version: Baseline | undefined,
  sortedRootElements: readonly OneOfElementListChildren[],
  sortBy: SortedElementGroupsSortBy,
  direction: SortDirection = sortBy === 'co2' || sortBy === 'cost'
    ? 'desc'
    : 'asc',
): SortedElementGroupSection[] => {
  if (!groupProps || !version) return [];

  const allRootElements = version.elements.filter(projectGuards.is.element);
  const allSections = groupProps.groupSectionBuilder(allRootElements);

  const sortedVisibleIds = sortedRootElements
    .filter(projectGuards.is.element)
    .map((el) => el.id);
  const visibleOrderById = new Map(
    sortedVisibleIds.map((id, index) => [id, index]),
  );

  const sections: SortedElementGroupSection[] = allSections
    .map((section) => {
      const visibleItems = section.items
        .filter((item) => visibleOrderById.has(item.id))
        .sort(
          (a, b) =>
            (visibleOrderById.get(a.id) ?? 0) -
            (visibleOrderById.get(b.id) ?? 0),
        );
      return { ...section, visibleItems };
    })
    .filter((section) => section.visibleItems.length > 0);

  if (sortBy === 'position')
    return direction === 'asc' ? sections : [...sections].reverse();
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...sections].sort((a, b) => {
    if (sortBy === 'name') {
      return multiplier * a.label.localeCompare(b.label);
    }
    const factor = sortBy === 'co2' ? 'co2e' : 'sek';
    return (
      multiplier *
      ((getConversionFactorValue(a.meta.resultsSum, factor) ?? 0) -
        (getConversionFactorValue(b.meta.resultsSum, factor) ?? 0))
    );
  });
};

/**
 * Memoized {@link getSortedElementGroupSections} for the elements list when
 * grouping (e.g. SBEF) is active.
 */
export const useSortedElementGroups = (
  params: UseSortedElementGroupsParams,
): SortedElementGroupSection[] => {
  const { groupProps, version, sortedRootElements, sortBy, direction } = params;
  return useMemo(
    () =>
      getSortedElementGroupSections(
        groupProps,
        version,
        sortedRootElements,
        sortBy,
        direction,
      ),
    [groupProps, version, sortedRootElements, sortBy, direction],
  );
};
